import { useTranslation } from "react-i18next"

import type {
  BrandingInterfaceValue,
  ColorModeChoice,
  UiOptionChoice,
} from "./branding-interface-utils"
import type { UiOptionKey } from "@/lib/runtime-branding"

import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SUPPORTED_LANGUAGES } from "@/lib/languages"
import { buildTimeUiOption } from "@/lib/ui-options"
import { DEFAULT_PRIVACY_POLICY_URL } from "@/locales/constants"

import {
  applyUiOptionChoice,
  choiceFor,
  COLOR_MODE_CHOICES,
  colorModeChoiceFor,
  colorModeFromChoice,
  offeredLanguages,
  toggleLanguage,
} from "./branding-interface-utils"

const COLOR_MODE_LABELS: Record<ColorModeChoice, { key: string; fallback: string }> = {
  unset: { key: "adminDashboard.branding.interface.colorModeUnset", fallback: "Not set (light)" },
  light: { key: "adminDashboard.branding.interface.colorModeLight", fallback: "Light" },
  dark: { key: "adminDashboard.branding.interface.colorModeDark", fallback: "Dark" },
  system: { key: "adminDashboard.branding.interface.colorModeSystem", fallback: "Follow system" },
}

const UI_OPTION_ROWS: {
  key: UiOptionKey
  labelKey: string
  labelFallback: string
  helpKey: string
  helpFallback: string
}[] = [
  {
    key: "show_dev_options",
    labelKey: "adminDashboard.branding.interface.devOptions",
    labelFallback: "Developer options",
    helpKey: "adminDashboard.branding.interface.devOptionsHelp",
    helpFallback:
      "Test-only choices: the quick research depth in AI Explorer and the dictionary upload in the translator.",
  },
  {
    key: "show_thought_process",
    labelKey: "adminDashboard.branding.interface.thoughtProcess",
    labelFallback: "Thought process tab",
    helpKey: "adminDashboard.branding.interface.thoughtProcessHelp",
    helpFallback: "Shows how an answer was reached in the answer's analysis panel.",
  },
  {
    key: "segment_demo_data",
    labelKey: "adminDashboard.branding.interface.segmentDemoData",
    labelFallback: "Segment Analysis demo data",
    helpKey: "adminDashboard.branding.interface.segmentDemoDataHelp",
    helpFallback:
      "Fills Segment Analysis with a demo portfolio while the environment has no segments of its own. Keep off for customers.",
  },
]

type BrandingInterfaceSectionProps = {
  value: BrandingInterfaceValue
  onChange: (next: BrandingInterfaceValue) => void
}

/**
 * Settings that used to need a customer branch. The default color mode
 * (#3298) is what users with no stored choice of their own open in. UI
 * switches (#3225) are three-way: follow the build (nothing stored), or an
 * explicit on/off that wins over the build. Languages (#3226) limit what the
 * switcher offers. The privacy policy link (#3243) is what the onboarding
 * consent text points to.
 */
export function BrandingInterfaceSection({ value, onChange }: BrandingInterfaceSectionProps) {
  const { t } = useTranslation()
  const onOff = (on: boolean) =>
    on
      ? t("adminDashboard.branding.interface.on", { defaultValue: "On" })
      : t("adminDashboard.branding.interface.off", { defaultValue: "Off" })

  const setChoice = (key: UiOptionKey, choice: UiOptionChoice) =>
    onChange({ ...value, ui_options: applyUiOptionChoice(value.ui_options, key, choice) })
  const offered = offeredLanguages(value.enabled_languages)

  return (
    <section className="grid gap-3">
      <h3 className="text-sm font-semibold">
        {t("adminDashboard.branding.interface.title", { defaultValue: "Interface" })}
      </h3>
      <div className="grid gap-4">
        <div className="grid gap-1.5 sm:grid-cols-[1fr_14rem] sm:items-start">
          <div className="grid gap-0.5">
            <Label htmlFor="branding-default-color-mode">
              {t("adminDashboard.branding.interface.defaultColorMode", {
                defaultValue: "Default color mode",
              })}
            </Label>
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.branding.interface.defaultColorModeHelp", {
                defaultValue:
                  "What people see before they pick light or dark themselves. Anyone who has already chosen keeps their choice.",
              })}
            </p>
          </div>
          <Select
            value={colorModeChoiceFor(value.default_color_mode)}
            onValueChange={(choice) =>
              onChange({
                ...value,
                default_color_mode: colorModeFromChoice(choice as ColorModeChoice),
              })
            }
          >
            <SelectTrigger id="branding-default-color-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COLOR_MODE_CHOICES.map((choice) => (
                <SelectItem key={choice} value={choice}>
                  {t(COLOR_MODE_LABELS[choice].key, {
                    defaultValue: COLOR_MODE_LABELS[choice].fallback,
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {UI_OPTION_ROWS.map((row) => {
          const id = `branding-ui-${row.key}`
          return (
            <div key={row.key} className="grid gap-1.5 sm:grid-cols-[1fr_14rem] sm:items-start">
              <div className="grid gap-0.5">
                <Label htmlFor={id}>{t(row.labelKey, { defaultValue: row.labelFallback })}</Label>
                <p className="text-muted-foreground text-xs">
                  {t(row.helpKey, { defaultValue: row.helpFallback })}
                </p>
              </div>
              <Select
                value={choiceFor(value.ui_options[row.key])}
                onValueChange={(choice) => setChoice(row.key, choice as UiOptionChoice)}
              >
                <SelectTrigger id={id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="build">
                    {t("adminDashboard.branding.interface.followBuild", {
                      defaultValue: "Default ({{value}})",
                      value: onOff(buildTimeUiOption(row.key)),
                    })}
                  </SelectItem>
                  <SelectItem value="on">{onOff(true)}</SelectItem>
                  <SelectItem value="off">{onOff(false)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )
        })}
        <div className="grid gap-1.5 sm:grid-cols-[1fr_14rem] sm:items-start">
          <div className="grid gap-0.5">
            <p className="text-sm font-medium">
              {t("adminDashboard.branding.interface.languages", { defaultValue: "Languages" })}
            </p>
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.branding.interface.languagesHelp", {
                defaultValue:
                  "The interface languages people can choose. Someone who had picked a language that is no longer offered gets Swedish, or the first offered language.",
              })}
            </p>
          </div>
          <div className="grid gap-2">
            {SUPPORTED_LANGUAGES.map((language) => {
              const checked = offered.includes(language.code)
              const id = `branding-language-${language.code}`
              return (
                <div key={language.code} className="flex items-center gap-2">
                  <Checkbox
                    checked={checked}
                    // The page needs at least one language.
                    disabled={checked && offered.length === 1}
                    id={id}
                    onCheckedChange={(next) =>
                      onChange({
                        ...value,
                        enabled_languages: toggleLanguage(
                          value.enabled_languages,
                          language.code,
                          next === true
                        ),
                      })
                    }
                  />
                  <Label className="font-normal" htmlFor={id}>
                    {language.label}
                  </Label>
                </div>
              )
            })}
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="branding-privacy-policy-url">
            {t("adminDashboard.branding.interface.privacyPolicyUrl", {
              defaultValue: "Privacy policy link",
            })}
          </Label>
          <Input
            id="branding-privacy-policy-url"
            placeholder={DEFAULT_PRIVACY_POLICY_URL}
            value={value.privacy_policy_url ?? ""}
            onChange={(event) =>
              onChange({ ...value, privacy_policy_url: event.target.value.trim() || null })
            }
          />
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.branding.interface.privacyPolicyUrlHelp", {
              defaultValue:
                "Where the cookie consent in onboarding links for the full privacy policy. Must start with https://. Leave blank for the default.",
            })}
          </p>
        </div>
      </div>
    </section>
  )
}
